import dayjs from 'dayjs';
import { useObservable, useObservableEagerState } from 'observable-hooks';
import {
	distinctUntilChanged,
	map,
	startWith,
	switchMap,
	takeWhile,
	timer,
} from 'rxjs';

import { useDTicketPriceConfig } from '../../contexts/DTicketPriceContext';

const MINUTE = 1000 * 60;
const DAY = MINUTE * 60 * 24;

export const useBasicTicketCost = (ticketValidFrom?: string) => {
	const { priceChangeDate, oldPrice, newPrice, dayBeforePriceChangeDate } =
		useDTicketPriceConfig();
	const priceChangeDateObj = dayjs(priceChangeDate);
	const dayBeforePriceChangeObj = dayjs(dayBeforePriceChangeDate);

	/**
	 * Uses the DTicket's `validFrom` date as a reference for `priceChangeDate` comparison.
	 * Falls back to the current date if no ticket is provided.
	 * @example
	 * ticket = {
	 *         "id": "aa5d53e9-d5b6-4b86-85f8-90b1a2396aea",
	 *         "status": "VALID",
	 *         "validFrom": "2025-12-31T23:00:00Z",
	 *         "validTo": "2026-02-01T02:00:00Z",
	 *         ...rest
	 *     }
	 * */
	const getRefDate = () => (ticketValidFrom ? dayjs(ticketValidFrom) : dayjs());

	const getCost = () =>
		getRefDate().isBefore(priceChangeDateObj) ? oldPrice : newPrice;

	const basicTicketCost$ = useObservable(() => {
		return timer(0, DAY).pipe(
			switchMap(() =>
				timer(
					0,
					getRefDate().isSame(dayBeforePriceChangeObj, 'day') ? MINUTE : DAY
				)
			),
			map(getCost),
			takeWhile(() => getRefDate().isBefore(priceChangeDateObj, 'day'), true),
			distinctUntilChanged(),
			startWith(getCost())
		);
	}, [priceChangeDate, oldPrice, newPrice]);

	const basicTicketCost = useObservableEagerState(basicTicketCost$);

	return {
		basicTicketCost,
		oldPrice,
		newPrice,
	};
};
